import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faRotate,
  faArrowsRotate,
  faLink,
  faPlus,
  faBan,
  faRotateLeft,
  faRightLeft,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import {
  fetchQueue, fetchNodes, fetchAliases, fetchSuggestions,
  linkSurface, mintForSurface, resyncOrphans, rejectSurface,
  repointAlias, setAliasActive, relabelNode, setNodeActive,
  type PersonaCurationQueue, type PersonaNode, type PersonaAlias,
  type UnresolvedPersonaItem, type SuggestedPersonaNode,
} from '../api/personaTaxonomy';

type Tab = 'queue' | 'aliases' | 'nodes';
type RunFn = (fn: () => Promise<unknown>, successMsg: string) => Promise<void>;

function errText(e: unknown, fallback: string): string {
  const resp = (e as { response?: { data?: { message?: string } } })?.response;
  return resp?.data?.message ?? fallback;
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
}

/**
 * Persona Taxonomy — curation of the emergent archetype set (personas_spec.md
 * §8), the persona analogue of the Skills Taxonomy. Reduced to persona-applicable
 * families and operations: an orphan queue of custom surfaces (link / mint /
 * reject), plus node and alias upkeep (relabel / repoint / deprecate). No
 * domains, senses, edges, or parents — personas have none (§12.5). Independent of
 * the legacy persona UI.
 */
export default function PersonaTaxonomy() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('queue');
  const [queue, setQueue] = useState<PersonaCurationQueue | null>(null);
  const [nodes, setNodes] = useState<PersonaNode[]>([]);
  const [aliases, setAliases] = useState<PersonaAlias[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [q, n, a] = await Promise.all([fetchQueue(), fetchNodes(), fetchAliases()]);
    setQueue(q); setNodes(n); setAliases(a);
  }, []);

  useEffect(() => {
    setLoading(true);
    load().catch((e) => setError(errText(e, t('personas.loadError', 'Could not load persona taxonomy.'))))
      .finally(() => setLoading(false));
  }, [load, t]);

  const run: RunFn = useCallback(async (fn, successMsg) => {
    setBusy(true); setError(null); setSuccess(null);
    try { await fn(); await load(); setSuccess(successMsg); }
    catch (e) { setError(errText(e, t('personas.actionFailed', 'Action failed.'))); }
    finally { setBusy(false); }
  }, [load, t]);

  const nodeLabel = useCallback(
    (id: string | null) => (id ? nodes.find((n) => n.id === id)?.canonical_label ?? id : '—'),
    [nodes],
  );

  const orphanCount = queue?.orphans.length ?? 0;
  const lintCount = queue?.active_aliases_on_deprecated_nodes.length ?? 0;

  if (loading) return <p className="p-8 text-center text-sm text-gray-400">{t('personas.loading', 'Loading…')}</p>;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">{t('personas.taxonomyTitle', 'Persona Taxonomy')}</h1>
            <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">{t('personas.curation', 'Curation')}</span>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {t('personas.taxonomySubtitle', 'Promote recurring custom archetypes into the canonical set, and keep aliases and nodes tidy.')}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button type="button" disabled={busy} onClick={() => run(() => resyncOrphans(), t('personas.resynced', 'Re-synced.'))}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
            <FontAwesomeIcon icon={faArrowsRotate} className="h-3.5 w-3.5" /> {t('personas.resync', 'Resync')}
          </button>
          <button type="button" disabled={busy} onClick={() => run(async () => {}, t('personas.refreshed', 'Refreshed.'))}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
            <FontAwesomeIcon icon={faRotate} className="h-3.5 w-3.5" /> {t('personas.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label={t('personas.inQueue', 'In Queue')} value={orphanCount + lintCount} />
        <StatCard label={t('personas.orphans', 'Custom archetypes')} value={orphanCount} />
        <StatCard label={t('personas.lint', 'Aliases on retired')} value={lintCount} />
        <StatCard label={t('personas.nodes', 'Canonical personas')} value={nodes.length} />
      </div>

      {/* Banners */}
      {success && <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700" role="status">{success}</div>}
      {error && (
        <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Tabs */}
      <nav className="border-b border-gray-200">
        <div className="flex gap-4 -mb-px" role="tablist">
          {(['queue', 'aliases', 'nodes'] as Tab[]).map((key) => (
            <button key={key} role="tab" type="button" aria-selected={tab === key} onClick={() => setTab(key)}
              className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${tab === key ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t(`personas.tab_${key}`, key)}
            </button>
          ))}
        </div>
      </nav>

      {tab === 'queue' && queue && <QueueTab queue={queue} nodes={nodes} busy={busy} nodeLabel={nodeLabel} run={run} />}
      {tab === 'aliases' && <AliasesTab aliases={aliases} nodes={nodes} busy={busy} nodeLabel={nodeLabel} run={run} />}
      {tab === 'nodes' && <NodesTab nodes={nodes} busy={busy} run={run} />}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

// ---- Queue tab ----

function QueueTab({ queue, nodes, busy, nodeLabel, run }: {
  queue: PersonaCurationQueue; nodes: PersonaNode[]; busy: boolean;
  nodeLabel: (id: string | null) => string; run: RunFn;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);
  const orphans = queue.orphans;
  const current = orphans.find((o) => o.surface === selected) ?? null;

  return (
    <div className="space-y-4">
      {queue.active_aliases_on_deprecated_nodes.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {t('personas.lintCallout', 'Some active aliases point at retired personas — repoint or reactivate them from the Aliases tab.')}
          {' '}
          {queue.active_aliases_on_deprecated_nodes.map((a) => (
            <code key={a.alias_id} className="mx-1 rounded bg-white px-1 py-0.5 text-xs">{a.surface_form} → {nodeLabel(a.node_id)}</code>
          ))}
        </div>
      )}

      {orphans.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-500">
          {t('personas.queueEmpty', 'No custom archetypes awaiting curation.')}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
          <ul className="max-h-[70vh] space-y-1.5 overflow-y-auto">
            {orphans.map((o) => (
              <li key={o.surface}>
                <button type="button" onClick={() => setSelected(o.surface)}
                  className={`w-full rounded-lg border-l-4 border border-gray-200 bg-white px-3 py-2 text-left hover:border-brand-300 ${selected === o.surface ? 'border-l-brand-500 ring-1 ring-brand-300' : 'border-l-purple-300'}`}>
                  <code className="text-sm font-medium text-gray-900">“{o.surface}”</code>
                  <div className="mt-0.5 text-[11px] text-gray-500">
                    {o.occurrence_count} {o.occurrence_count === 1 ? t('personas.tender', 'tender') : t('personas.tenders', 'tenders')} · {timeAgo(o.last_seen)}
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <div className="rounded-xl border border-gray-200 p-4">
            {current ? <OrphanDetail item={current} nodes={nodes} busy={busy} run={run} onDone={() => setSelected(null)} />
              : <p className="py-12 text-center text-sm text-gray-400">{t('personas.selectItem', 'Select a custom archetype.')}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function OrphanDetail({ item, nodes, busy, run, onDone }: {
  item: UnresolvedPersonaItem; nodes: PersonaNode[]; busy: boolean; run: RunFn; onDone: () => void;
}) {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState<SuggestedPersonaNode[]>([]);
  const [nodeSearch, setNodeSearch] = useState('');
  const [mintLabel, setMintLabel] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchSuggestions(item.surface).then((s) => { if (!cancelled) setSuggestions(s); }).catch(() => {});
    setNodeSearch(''); setMintLabel(item.surface);
    return () => { cancelled = true; };
  }, [item.surface]);

  const matches = useMemo(() => {
    const q = nodeSearch.trim().toLowerCase();
    if (!q) return [];
    return nodes.filter((n) => n.canonical_label.toLowerCase().includes(q)).slice(0, 8);
  }, [nodeSearch, nodes]);

  const link = (nodeId: string) => run(() => linkSurface(item.surface, nodeId), t('personas.linked', 'Linked.')).then(onDone);
  const mint = () => run(() => mintForSurface(item.surface, mintLabel.trim()), t('personas.minted', 'Minted.')).then(onDone);
  const reject = () => { if (window.confirm(t('personas.confirmReject', 'Reject this surface? It will be hidden from the queue.'))) run(() => rejectSurface(item.surface), t('personas.rejected', 'Rejected.')).then(onDone); };

  return (
    <div className="space-y-4">
      <div>
        <code className="text-base font-semibold text-gray-900">“{item.surface}”</code>
        <p className="mt-0.5 text-[11px] text-gray-500">
          {item.occurrence_count} {t('personas.tenders', 'tenders')} · {t('personas.firstSeen', 'first seen')} {timeAgo(item.first_seen)}
        </p>
      </div>

      {suggestions.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-gray-500">{t('personas.suggested', 'Suggested matches')}</p>
          <div className="space-y-1">
            {suggestions.map((s) => (
              <div key={s.node_id} className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-1.5">
                <span className="text-sm text-gray-800">{s.label}</span>
                <button type="button" disabled={busy} onClick={() => link(s.node_id)}
                  className="inline-flex items-center gap-1 rounded bg-brand-600 px-2 py-1 text-xs text-white hover:bg-brand-700 disabled:opacity-50">
                  <FontAwesomeIcon icon={faLink} className="h-3 w-3" /> {t('personas.link', 'Link')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="mb-1 text-xs font-medium text-gray-500">{t('personas.linkExisting', 'Link to an existing persona')}</p>
        <div className="relative">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input value={nodeSearch} onChange={(e) => setNodeSearch(e.target.value)} placeholder={t('personas.searchPersonas', 'Search personas')}
            className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-brand-400 focus:outline-none" />
        </div>
        {matches.length > 0 && (
          <div className="mt-1 max-h-44 space-y-1 overflow-y-auto">
            {matches.map((n) => (
              <button key={n.id} type="button" disabled={busy} onClick={() => link(n.id)}
                className="flex w-full items-center justify-between rounded-lg border border-gray-100 px-3 py-1.5 text-left text-sm hover:bg-gray-50 disabled:opacity-50">
                {n.canonical_label}
                <FontAwesomeIcon icon={faLink} className="h-3 w-3 text-gray-400" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="mb-1 text-xs font-medium text-gray-500">{t('personas.mintNew', 'Mint a new persona')}</p>
        <div className="flex gap-2">
          <input value={mintLabel} onChange={(e) => setMintLabel(e.target.value)}
            className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" />
          <button type="button" disabled={busy || !mintLabel.trim()} onClick={mint}
            className="inline-flex items-center gap-1 rounded-lg bg-brand-600 px-3 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-50">
            <FontAwesomeIcon icon={faPlus} className="h-3 w-3" /> {t('personas.mintLink', 'Mint & Link')}
          </button>
        </div>
      </div>

      <div className="border-t border-gray-100 pt-3">
        <button type="button" disabled={busy} onClick={reject}
          className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50">
          <FontAwesomeIcon icon={faBan} className="h-3 w-3" /> {t('personas.reject', 'Reject')}
        </button>
      </div>
    </div>
  );
}

// ---- Aliases tab ----

function AliasesTab({ aliases, nodes, busy, nodeLabel, run }: {
  aliases: PersonaAlias[]; nodes: PersonaNode[]; busy: boolean;
  nodeLabel: (id: string | null) => string; run: RunFn;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? aliases.filter((a) => a.surface_form.toLowerCase().includes(q) || nodeLabel(a.node_id).toLowerCase().includes(q)) : aliases;
  }, [aliases, search, nodeLabel]);

  return (
    <div className="space-y-3">
      <div className="relative max-w-[340px]">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('personas.searchAliases', 'Search aliases')}
          className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-brand-400 focus:outline-none" />
      </div>
      {shown.length === 0 ? (
        <p className="p-6 text-center text-sm text-gray-400">{t('personas.noAliases', 'No aliases.')}</p>
      ) : (
        <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
          {shown.map((a) => <AliasManageRow key={a.id} alias={a} nodes={nodes} busy={busy} nodeLabel={nodeLabel} run={run} />)}
        </div>
      )}
    </div>
  );
}

function AliasManageRow({ alias, nodes, busy, nodeLabel, run }: {
  alias: PersonaAlias; nodes: PersonaNode[]; busy: boolean;
  nodeLabel: (id: string | null) => string; run: RunFn;
}) {
  const { t } = useTranslation();
  const [repointing, setRepointing] = useState(false);
  const deprecated = alias.status !== 'ACTIVE';
  return (
    <div className="flex items-center gap-2 px-4 py-2.5">
      <div className="min-w-0 flex-1">
        <code className={`text-sm ${deprecated ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{alias.surface_form}</code>
        <span className="text-xs text-gray-400"> → {nodeLabel(alias.node_id)}</span>
        {deprecated && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-500">{t('personas.deprecated', 'deprecated')}</span>}
      </div>
      {repointing ? (
        <select defaultValue="" onChange={(e) => e.target.value && run(() => repointAlias(alias.id, e.target.value), t('personas.repointed', 'Repointed.')).then(() => setRepointing(false))}
          onBlur={() => setRepointing(false)} className="rounded border border-gray-200 px-2 py-1 text-xs">
          <option value="" disabled>{t('personas.repointTo', 'Repoint to…')}</option>
          {nodes.filter((n) => n.id !== alias.node_id).map((n) => <option key={n.id} value={n.id}>{n.canonical_label}</option>)}
        </select>
      ) : (
        <button type="button" disabled={busy} onClick={() => setRepointing(true)} aria-label={t('personas.repoint', 'Repoint')} className="text-gray-400 hover:text-brand-600 disabled:opacity-50">
          <FontAwesomeIcon icon={faRightLeft} className="h-3.5 w-3.5" />
        </button>
      )}
      <button type="button" disabled={busy} onClick={() => run(() => setAliasActive(alias.id, deprecated), deprecated ? t('personas.reactivated', 'Reactivated.') : t('personas.deprecatedMsg', 'Deprecated.'))}
        aria-label={deprecated ? t('personas.reactivate', 'Reactivate') : t('personas.deprecate', 'Deprecate')}
        className={`${deprecated ? 'text-green-600' : 'text-red-500'} disabled:opacity-50`}>
        <FontAwesomeIcon icon={deprecated ? faRotateLeft : faBan} className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ---- Nodes tab ----

function NodesTab({ nodes, busy, run }: { nodes: PersonaNode[]; busy: boolean; run: RunFn }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? nodes.filter((n) => n.canonical_label.toLowerCase().includes(q)) : nodes;
  }, [nodes, search]);

  return (
    <div className="space-y-3">
      <div className="relative max-w-[340px]">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('personas.searchPersonas', 'Search personas')}
          className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-brand-400 focus:outline-none" />
      </div>
      {shown.length === 0 ? (
        <p className="p-6 text-center text-sm text-gray-400">{t('personas.noNodes', 'No canonical personas yet.')}</p>
      ) : (
        <div className="space-y-2">
          {shown.map((n) => <NodeCard key={n.id} node={n} busy={busy} run={run} />)}
        </div>
      )}
    </div>
  );
}

function NodeCard({ node, busy, run }: { node: PersonaNode; busy: boolean; run: RunFn }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(node.canonical_label);
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-4">
      {editing ? (
        <div className="flex flex-1 gap-2">
          <input value={label} onChange={(e) => setLabel(e.target.value)} className="flex-1 rounded border border-gray-200 px-2 py-1 text-sm" />
          <button type="button" disabled={busy || !label.trim()} onClick={() => run(() => relabelNode(node.id, label.trim()), t('personas.relabeled', 'Relabeled.')).then(() => setEditing(false))}
            className="rounded bg-brand-600 px-2 py-1 text-xs text-white disabled:opacity-50">{t('common.save', 'Save')}</button>
          <button type="button" onClick={() => { setLabel(node.canonical_label); setEditing(false); }} className="rounded border border-gray-200 px-2 py-1 text-xs">{t('common.cancel', 'Cancel')}</button>
        </div>
      ) : (
        <span className="font-medium text-gray-900">{node.canonical_label}</span>
      )}
      <div className="flex shrink-0 gap-3">
        {!editing && (
          <button type="button" onClick={() => setEditing(true)} aria-label={t('personas.relabel', 'Relabel')} className="text-gray-400 hover:text-brand-600">
            <FontAwesomeIcon icon={faPen} className="h-3.5 w-3.5" />
          </button>
        )}
        <button type="button" disabled={busy} onClick={() => run(() => setNodeActive(node.id, false), t('personas.deprecatedMsg', 'Deprecated.'))}
          aria-label={t('personas.deprecate', 'Deprecate')} className="text-red-500 disabled:opacity-50">
          <FontAwesomeIcon icon={faBan} className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
